import { ILayout, ILayoutElement, LayoutElementType } from '../layout';
import { IHeaderLink } from '../headerLinksConfig';

// AC-952 / S3.4 — starter templates for the header/footer layout editor.
//
// Each template's build() returns a fresh ILayout with newly-generated element ids, so a user starts
// from a realistic, common header/footer shape instead of an empty grid, then tweaks. Templates are
// region-specific. The sample links/text are placeholders the user is expected to edit.

let seq = 0;
const genId = (type: string): string => `${type}-tpl-${(seq++).toString(36)}`;

const el = (type: LayoutElementType, props?: Record<string, unknown>): ILayoutElement => ({ id: genId(type), type, props: props ?? {} });
const link = (label: string, url: string): IHeaderLink => ({ id: genId('link'), label, url });
const linkGroup = (links: IHeaderLink[]): ILayoutElement => el('linkGroup', { links });
const text = (value: string): ILayoutElement => el('text', { text: value });

export interface ILayoutTemplate {
    key: string;
    name: string;
    description: string;
    build: () => ILayout;
}

const NAV = (): IHeaderLink[] => [link('Home', '/'), link('Products', '/products'), link('About', '/about'), link('Contact', '/contact')];

export const HEADER_TEMPLATES: ILayoutTemplate[] = [
    {
        key: 'classic',
        name: 'Classic',
        description: 'Logo left, links centered, search + cart + account right.',
        build: () => ({
            rows: [{
                columns: [
                    { width: 'auto', elements: [el('logo')] },
                    { width: '1fr', align: 'center', elements: [linkGroup(NAV())] },
                    { width: 'auto', align: 'right', elements: [el('search'), el('cart'), el('account')] },
                ],
            }],
        }),
    },
    {
        key: 'centered-logo',
        name: 'Centered logo',
        description: 'Links left, logo centered, search + cart + account right.',
        build: () => ({
            rows: [{
                columns: [
                    { width: '1fr', elements: [linkGroup(NAV())] },
                    { width: 'auto', align: 'center', elements: [el('logo')] },
                    { width: '1fr', align: 'right', elements: [el('search'), el('cart'), el('account')] },
                ],
            }],
        }),
    },
    {
        key: 'search-forward',
        name: 'Search-forward',
        description: 'Logo left, a wide search bar in the middle, cart + account right.',
        build: () => ({
            rows: [{
                columns: [
                    { width: 'auto', elements: [el('logo')] },
                    { width: '1fr', elements: [el('search')] },
                    { width: 'auto', align: 'right', elements: [el('cart'), el('account')] },
                ],
            }],
        }),
    },
    {
        key: 'minimal',
        name: 'Minimal',
        description: 'Logo left, a flexible gap, cart + account right.',
        build: () => ({
            rows: [{
                columns: [
                    { width: 'auto', elements: [el('logo')] },
                    { width: '1fr', elements: [el('spacer')] },
                    { width: 'auto', align: 'right', elements: [el('cart'), el('account')] },
                ],
            }],
        }),
    },
];

export const FOOTER_TEMPLATES: ILayoutTemplate[] = [
    {
        key: 'simple-bar',
        name: 'Simple bar',
        description: 'Logo, a centered copyright line, and legal links.',
        build: () => ({
            rows: [{
                columns: [
                    { width: 'auto', elements: [el('logo')] },
                    { width: '1fr', align: 'center', elements: [text('© Your Company. All rights reserved.')] },
                    { width: 'auto', align: 'right', elements: [linkGroup([link('Privacy', '/privacy'), link('Terms', '/terms')])] },
                ],
            }],
        }),
    },
    {
        key: 'link-columns',
        name: 'Link columns',
        description: 'Three columns of links (Company, Support, Legal).',
        build: () => ({
            rows: [{
                columns: [
                    { elements: [linkGroup([link('About', '/about'), link('Careers', '/careers'), link('Contact', '/contact')])] },
                    { elements: [linkGroup([link('Support', '/support'), link('FAQ', '/faq'), link('Order status', '/orders')])] },
                    { elements: [linkGroup([link('Privacy', '/privacy'), link('Terms', '/terms')])] },
                ],
            }],
        }),
    },
    {
        key: 'stacked-centered',
        name: 'Stacked & centered',
        description: 'Logo, links, then a copyright line — each centered on its own row.',
        build: () => ({
            rows: [
                { columns: [{ width: '1fr', align: 'center', elements: [el('logo')] }] },
                { columns: [{ width: '1fr', align: 'center', elements: [linkGroup([link('Privacy', '/privacy'), link('Terms', '/terms'), link('Contact', '/contact')])] }] },
                { columns: [{ width: '1fr', align: 'center', elements: [text('© Your Company. All rights reserved.')] }] },
            ],
        }),
    },
];

export const templatesFor = (region: 'header' | 'footer'): ILayoutTemplate[] => (region === 'header' ? HEADER_TEMPLATES : FOOTER_TEMPLATES);
